/**
 * Project detail page — /projects/:name
 *
 * Tabs: Overview · Commits · Branches · Files
 */

import { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  GitBranch,
  Folder,
  FileText,
  History,
  ChevronRight,
  ArrowLeft,
  RefreshCw,
  Archive,
  FolderGit2,
  GitCommit,
  Loader2,
  ChevronUp,
} from "lucide-react";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { StatusDot } from "../components/ui/StatusDot";
import { HexBadge } from "../components/ui/HexBadge";
import { GlyphButton } from "../components/ui/GlyphButton";

// ─── Types ────────────────────────────────────────────────────────────────────

interface GitCommitInfo {
  hash: string;
  fullHash: string;
  message: string;
  date: string;
  author: string;
}

type ProjectKind = "worktree" | "bare";
type ProjectStatus = "clean" | "dirty" | "ahead" | "behind" | "bare";

interface Project {
  name: string;
  path: string;
  kind: ProjectKind;
  branch: string;
  status: ProjectStatus;
  lastCommit: GitCommitInfo | null;
  hasWorktree: boolean;
  worktreePath: string | null;
}

interface BranchInfo {
  name: string;
  current: boolean;
  hash: string;
  subject: string;
}

interface TreeEntry {
  name: string;
  type: "blob" | "tree";
  hash: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getStatusVariant(status: ProjectStatus): "online" | "warning" | "offline" {
  switch (status) {
    case "clean": return "online";
    case "dirty":
    case "ahead":
    case "behind": return "warning";
    case "bare": return "offline";
  }
}

function getStatusLabel(status: ProjectStatus): string {
  switch (status) {
    case "clean": return "CLEAN";
    case "dirty": return "UNCOMMITTED";
    case "ahead": return "AHEAD";
    case "behind": return "BEHIND";
    case "bare": return "NOT CLONED";
  }
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

function formatDateShort(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

// ─── Tab bar ──────────────────────────────────────────────────────────────────

type Tab = "overview" | "commits" | "branches" | "files";

interface TabDef {
  id: Tab;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
}

const TABS: TabDef[] = [
  { id: "overview", label: "OVERVIEW", icon: FolderGit2 },
  { id: "commits", label: "COMMITS", icon: History },
  { id: "branches", label: "BRANCHES", icon: GitBranch },
  { id: "files", label: "FILES", icon: Folder },
];

function TabBar({ active, onChange }: { active: Tab; onChange: (t: Tab) => void }) {
  return (
    <div className="flex gap-0 border-b border-[var(--bw-border)] mb-6 overflow-x-auto">
      {TABS.map(({ id, label, icon: Icon }) => {
        const isActive = active === id;
        return (
          <button
            key={id}
            onClick={() => onChange(id)}
            className={`
              flex items-center gap-1.5 px-4 py-2.5 text-xs font-mono uppercase tracking-wider
              border-b-2 transition-all whitespace-nowrap flex-shrink-0
              ${isActive
                ? "border-[var(--bw-accent)] text-[var(--bw-accent)] bg-[var(--bw-surface)]"
                : "border-transparent text-[var(--bw-muted)] hover:text-[var(--bw-fg)] hover:border-[var(--bw-border)]"
              }
            `}
          >
            <Icon size={13} />
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────────

function OverviewTab({ project }: { project: Project }) {
  const rows: { label: string; value: string }[] = [
    { label: "NOMBRE", value: project.name },
    { label: "TIPO", value: project.kind.toUpperCase() },
    { label: "RUTA", value: project.path },
    { label: "RAMA", value: project.branch },
    { label: "ESTADO", value: getStatusLabel(project.status) },
    ...(project.worktreePath ? [{ label: "WORKTREE", value: project.worktreePath }] : []),
  ];

  return (
    <div className="space-y-4">
      <CorpoCard>
        <div className="text-xs uppercase tracking-wider text-[var(--bw-muted)] mb-4 font-mono">
          ▸ INFORMACIÓN DEL REPOSITORIO
        </div>
        <dl className="space-y-3">
          {rows.map(({ label, value }) => (
            <div key={label} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
              <dt className="text-[10px] font-mono uppercase tracking-widest text-[var(--bw-muted)] sm:w-28 flex-shrink-0 pt-0.5">
                {label}
              </dt>
              <dd className="text-sm font-mono text-[var(--bw-fg)] break-all">{value}</dd>
            </div>
          ))}
        </dl>
      </CorpoCard>

      {project.lastCommit && (
        <CorpoCard>
          <div className="text-xs uppercase tracking-wider text-[var(--bw-muted)] mb-4 font-mono">
            ▸ ÚLTIMO COMMIT
          </div>
          <div className="space-y-3">
            <code className="text-[var(--bw-accent)] font-mono text-sm break-all">
              {project.lastCommit.fullHash}
            </code>
            <p className="text-sm text-[var(--bw-fg)]">{project.lastCommit.message}</p>
            <div className="flex gap-4 text-xs text-[var(--bw-muted)] font-mono flex-wrap">
              <span>✎ {project.lastCommit.author}</span>
              <span>◷ {formatDate(project.lastCommit.date)}</span>
            </div>
          </div>
        </CorpoCard>
      )}
    </div>
  );
}

// ─── Commits tab ──────────────────────────────────────────────────────────────

function CommitsTab({ projectName }: { projectName: string }) {
  const [limit, setLimit] = useState(25);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["project-commits-detail", projectName, limit],
    queryFn: async () => {
      const res = await fetch(`/api/projects/${projectName}/commits?limit=${limit}`);
      return res.json() as Promise<{ commits: GitCommitInfo[]; error?: string }>;
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-mono text-[var(--bw-muted)] uppercase tracking-wider">
          {data?.commits?.length ?? 0} commits
        </span>
        <GlyphButton variant="ghost" size="sm" onClick={() => refetch()}>
          <RefreshCw size={12} />
        </GlyphButton>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-[var(--bw-muted)] text-sm">
          <Loader2 size={14} className="animate-spin" />
          CARGANDO...
        </div>
      )}

      {error && (
        <CorpoCard className="border-[var(--bw-danger)]">
          <p className="text-[var(--bw-danger)] text-sm">⚠ {String(error)}</p>
        </CorpoCard>
      )}

      {data?.commits && data.commits.length > 0 && (
        <CorpoCard className="p-0 overflow-hidden">
          <div className="divide-y divide-[var(--bw-border)]">
            {data.commits.map((commit, idx) => (
              <div
                key={commit.fullHash + idx}
                className="px-4 py-3 hover:bg-[var(--bw-surface)] transition-colors"
              >
                <div className="flex items-start gap-3">
                  <GitCommit
                    size={14}
                    className="text-[var(--bw-muted)] mt-0.5 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <code className="text-[var(--bw-accent)] font-mono text-xs flex-shrink-0">
                        {commit.hash}
                      </code>
                      <span className="text-xs text-[var(--bw-muted)] font-mono">
                        {commit.author}
                      </span>
                      <span className="text-xs text-[var(--bw-muted)] font-mono ml-auto flex-shrink-0">
                        {formatDateShort(commit.date)}
                      </span>
                    </div>
                    <p className="text-sm text-[var(--bw-fg)] break-words">{commit.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CorpoCard>
      )}

      {data?.commits && data.commits.length >= limit && (
        <div className="flex justify-center">
          <GlyphButton
            variant="secondary"
            size="sm"
            onClick={() => setLimit((l) => l + 25)}
          >
            CARGAR MÁS
          </GlyphButton>
        </div>
      )}
    </div>
  );
}

// ─── Branches tab ─────────────────────────────────────────────────────────────

function BranchesTab({ projectName }: { projectName: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["project-branches", projectName],
    queryFn: async () => {
      const res = await fetch(`/api/projects/${projectName}/branches`);
      return res.json() as Promise<{ branches: BranchInfo[]; error?: string }>;
    },
  });

  return (
    <div className="space-y-4">
      {isLoading && (
        <div className="flex items-center gap-2 text-[var(--bw-muted)] text-sm">
          <Loader2 size={14} className="animate-spin" />
          CARGANDO...
        </div>
      )}

      {error && (
        <CorpoCard className="border-[var(--bw-danger)]">
          <p className="text-[var(--bw-danger)] text-sm">⚠ {String(error)}</p>
        </CorpoCard>
      )}

      {data?.branches && data.branches.length > 0 && (
        <CorpoCard className="p-0 overflow-hidden">
          <div className="divide-y divide-[var(--bw-border)]">
            {data.branches.map((branch) => (
              <div
                key={branch.name}
                className={`px-4 py-3 flex items-center gap-3 ${
                  branch.current ? "bg-[var(--bw-surface)]" : "hover:bg-[var(--bw-surface)]"
                } transition-colors`}
              >
                <GitBranch
                  size={14}
                  className={branch.current ? "text-[var(--bw-accent)]" : "text-[var(--bw-muted)]"}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`text-sm font-mono font-bold ${
                        branch.current ? "text-[var(--bw-accent)]" : "text-[var(--bw-fg)]"
                      }`}
                    >
                      {branch.name}
                    </span>
                    {branch.current && (
                      <span className="text-[10px] font-mono bg-[var(--bw-accent)] text-[var(--bw-bg)] px-1.5 py-0.5 uppercase tracking-wider">
                        CURRENT
                      </span>
                    )}
                  </div>
                  {branch.subject && (
                    <p className="text-xs text-[var(--bw-muted)] mt-0.5 truncate">{branch.subject}</p>
                  )}
                </div>
                <code className="text-xs font-mono text-[var(--bw-muted)] flex-shrink-0">
                  {branch.hash}
                </code>
              </div>
            ))}
          </div>
        </CorpoCard>
      )}

      {data?.branches?.length === 0 && (
        <CorpoCard>
          <p className="text-[var(--bw-muted)] text-sm text-center py-4">NO HAY RAMAS</p>
        </CorpoCard>
      )}
    </div>
  );
}

// ─── Files tab ────────────────────────────────────────────────────────────────

function FilesTab({ projectName }: { projectName: string }) {
  const [currentPath, setCurrentPath] = useState("");
  const pathParts = currentPath ? currentPath.split("/") : [];

  const { data, isLoading, error } = useQuery({
    queryKey: ["project-tree", projectName, currentPath],
    queryFn: async () => {
      const qs = currentPath ? `?path=${encodeURIComponent(currentPath)}` : "";
      const res = await fetch(`/api/projects/${projectName}/tree${qs}`);
      return res.json() as Promise<{ entries: TreeEntry[]; path: string; error?: string }>;
    },
  });

  const sorted = data?.entries
    ? [...data.entries].sort((a, b) => {
        if (a.type !== b.type) return a.type === "tree" ? -1 : 1;
        return a.name.localeCompare(b.name);
      })
    : [];

  const navigateInto = (entry: TreeEntry) => {
    if (entry.type === "tree") {
      setCurrentPath(currentPath ? `${currentPath}/${entry.name}` : entry.name);
    }
  };

  const navigateUp = () => {
    const parts = currentPath.split("/");
    parts.pop();
    setCurrentPath(parts.join("/"));
  };

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-xs font-mono text-[var(--bw-muted)] flex-wrap">
        <button
          className="hover:text-[var(--bw-accent)] transition-colors"
          onClick={() => setCurrentPath("")}
        >
          {projectName}
        </button>
        {pathParts.map((part, idx) => (
          <span key={idx} className="flex items-center gap-1">
            <ChevronRight size={11} />
            <button
              className="hover:text-[var(--bw-accent)] transition-colors"
              onClick={() => setCurrentPath(pathParts.slice(0, idx + 1).join("/"))}
            >
              {part}
            </button>
          </span>
        ))}
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-[var(--bw-muted)] text-sm">
          <Loader2 size={14} className="animate-spin" />
          CARGANDO...
        </div>
      )}

      {error && (
        <CorpoCard className="border-[var(--bw-danger)]">
          <p className="text-[var(--bw-danger)] text-sm">⚠ {String(error)}</p>
        </CorpoCard>
      )}

      {!isLoading && (
        <CorpoCard className="p-0 overflow-hidden">
          <div className="divide-y divide-[var(--bw-border)]">
            {currentPath && (
              <button
                onClick={navigateUp}
                className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-[var(--bw-surface)] transition-colors text-left"
              >
                <ChevronUp size={14} className="text-[var(--bw-muted)]" />
                <span className="text-sm font-mono text-[var(--bw-muted)]">..</span>
              </button>
            )}

            {sorted.length === 0 && (
              <div className="px-4 py-6 text-center text-[var(--bw-muted)] text-sm">
                DIRECTORIO VACÍO
              </div>
            )}

            {sorted.map((entry) => (
              <button
                key={entry.name}
                onClick={() => navigateInto(entry)}
                disabled={entry.type === "blob"}
                className={`w-full px-4 py-2.5 flex items-center gap-3 transition-colors text-left ${
                  entry.type === "tree"
                    ? "hover:bg-[var(--bw-surface)] cursor-pointer"
                    : "cursor-default opacity-80"
                }`}
              >
                {entry.type === "tree" ? (
                  <Folder size={14} className="text-[var(--bw-accent)] flex-shrink-0" />
                ) : (
                  <FileText size={14} className="text-[var(--bw-muted)] flex-shrink-0" />
                )}
                <span className={`text-sm font-mono flex-1 min-w-0 truncate ${
                  entry.type === "tree" ? "text-[var(--bw-fg)] font-semibold" : "text-[var(--bw-fg)]"
                }`}>
                  {entry.name}{entry.type === "tree" ? "/" : ""}
                </span>
                {entry.type === "tree" && (
                  <ChevronRight size={12} className="text-[var(--bw-muted)] flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        </CorpoCard>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ProjectDetailRoute() {
  const { name } = useParams({ strict: false }) as { name: string };
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>("overview");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["project-detail", name],
    queryFn: async () => {
      const res = await fetch(`/api/projects/${encodeURIComponent(name)}`);
      if (!res.ok) throw new Error("Proyecto no encontrado");
      return res.json() as Promise<{ project: Project; error?: string }>;
    },
    enabled: !!name,
  });

  const project = data?.project;

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-5xl mx-auto">
      {/* Back + header */}
      <div className="flex items-start gap-4 flex-wrap">
        <GlyphButton
          variant="ghost"
          size="sm"
          icon={<ArrowLeft size={14} />}
          onClick={() => navigate({ to: "/projects" })}
        >
          REPOS
        </GlyphButton>

        <div className="flex-1 min-w-0">
          {isLoading && (
            <div className="flex items-center gap-2 text-[var(--bw-muted)]">
              <Loader2 size={16} className="animate-spin" />
              <span className="font-mono text-sm">CARGANDO...</span>
            </div>
          )}

          {project && (
            <div className="space-y-1">
              <div className="flex items-center gap-3 flex-wrap">
                {project.kind === "bare" ? (
                  <Archive size={18} className="text-[var(--bw-muted)] flex-shrink-0" />
                ) : (
                  <FolderGit2 size={18} className="text-[var(--bw-accent)] flex-shrink-0" />
                )}
                <CRTHeader text={project.name} level={2} />
                <HexBadge label={project.branch} variant="accent" />
                <StatusDot
                  status={getStatusVariant(project.status)}
                  size="sm"
                  label={getStatusLabel(project.status)}
                />
              </div>
              <p className="text-[10px] font-mono text-[var(--bw-muted)] pl-7">
                {project.path}
              </p>
            </div>
          )}
        </div>

        {project && (
          <GlyphButton
            variant="secondary"
            size="sm"
            icon={<RefreshCw size={13} />}
            onClick={() => refetch()}
          >
            SYNC
          </GlyphButton>
        )}
      </div>

      {error && (
        <CorpoCard className="border-[var(--bw-danger)]">
          <p className="text-[var(--bw-danger)]">
            ⚠ {error instanceof Error ? error.message : "Error desconocido"}
          </p>
        </CorpoCard>
      )}

      {project && (
        <>
          <TabBar active={activeTab} onChange={setActiveTab} />

          {activeTab === "overview" && <OverviewTab project={project} />}
          {activeTab === "commits" && <CommitsTab projectName={name} />}
          {activeTab === "branches" && <BranchesTab projectName={name} />}
          {activeTab === "files" && <FilesTab projectName={name} />}
        </>
      )}
    </div>
  );
}
